// VoidInvoiceModal.jsx

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Local
import { voidInvoice } from 'nodetypes/invoice/lifecycle/voidInvoice';
import { formatPrice } from 'utilities/Currency';
import {
  filterDocumentPayments,
  sumPaidDocumentPayments,
} from 'nodetypes/documents/payments/documentPaymentsModel';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Modal from 'components/modal/Modal';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import TextInput from 'components/textinput/TextInput';

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Confirm void (and optional Void & Duplicate).
 */
const VoidInvoiceModal = React.memo(({
  isOpen = false,
  onClose,
  invoiceNode = null,
  invoiceType = null,
  neighborNodes = [],
  duplicate = false,
  onVoided = null,
}) => {
  const isMobile = useIsMobile();

  const [reason, setReason] = useState('');
  const [notifyClient, setNotifyClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const pendingCreditCents = sumPaidDocumentPayments(
    filterDocumentPayments(neighborNodes),
  );

  useEffect(() => {
    if (!isOpen) return;
    setReason('');
    setNotifyClient(false);
    setBusy(false);
    setError('');
  }, [isOpen, duplicate]);

  const handleSubmit = useCallback(async () => {
    if (busy || !invoiceNode?.key) return;
    if (!cleanString(reason)) {
      setError('Enter a reason for voiding.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const result = await voidInvoice({
        invoiceNode,
        invoiceType,
        neighborNodes,
        reason,
        notifyClient,
      });

      const creditNote = result.pendingCreditCents > 0
        ? ` ${formatPrice(result.pendingCreditCents)} marked as pending credit.`
        : '';
      toast.success(
        duplicate
          ? `Invoice voided.${creditNote}`
          : `Invoice voided.${creditNote}`,
        { autoClose: 3500 },
      );

      if (typeof onVoided === 'function') {
        await onVoided({ ...result, duplicate });
      }
      onClose?.();
    } catch (err) {
      const message = err?.message || 'Could not void invoice.';
      setError(message);
      toast.error(message, { autoClose: 4000 });
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    duplicate,
    invoiceNode,
    invoiceType,
    neighborNodes,
    notifyClient,
    onClose,
    onVoided,
    reason,
  ]);

  if (!isOpen || !invoiceNode) return null;

  return (
    <Modal
      title={duplicate ? 'Void & Duplicate' : 'Void Invoice'}
      iconKey="block"
      isOpen={isOpen}
      onClose={busy ? undefined : onClose}
      width={isMobile ? '100%' : '480px'}
      maxHeight={isMobile ? '100%' : '90vh'}
    >
      <ModalForm>
        <ModalHint>
          {duplicate
            ? 'Voids this invoice and creates a new draft copy (line items only). Unpaid schedule lines are canceled. Paid amounts become pending credit until credits ship.'
            : 'Voids this invoice. Unpaid schedule lines are canceled. Paid amounts become pending credit until credits ship.'}
          {pendingCreditCents > 0
            ? ` Paid to date: ${formatPrice(pendingCreditCents)}.`
            : ''}
        </ModalHint>

        <ModalField label="Reason">
          <TextInput
            value={reason}
            onChange={(event) => setReason(event?.target?.value ?? event ?? '')}
            placeholder="Why is this invoice voided?"
            disabled={busy}
          />
        </ModalField>

        <ModalField label="">
          <Checkbox
            value={notifyClient}
            onChange={() => setNotifyClient((prev) => !prev)}
            label="Notify client (email when Send ships)"
          />
        </ModalField>

        {error ? <ModalHint>{error}</ModalHint> : null}
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="END">
          <Button
            label="Cancel"
            onClick={onClose}
            enabled={!busy}
          />
          <Button
            label={busy ? 'Voiding…' : (duplicate ? 'Void & Duplicate' : 'Void Invoice')}
            iconKey="block"
            onClick={handleSubmit}
            enabled={!busy}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

VoidInvoiceModal.displayName = 'VoidInvoiceModal';

export default VoidInvoiceModal;
